/* src/components/testing/countingSpan/StimulusDisplay.js */
import React from 'react';
import PropTypes from 'prop-types';

// Define colors (consider moving to constants/theme if used elsewhere)
const COLORS = {
    blue: '#3b82f6', // Tailwind blue-500
    green: '#22c55e', // Tailwind green-500
};
// Define stroke properties for visual separation on potentially complex backgrounds
const STROKE_COLOR = '#FFFFFF'; // White outline - adjust if background changes
const STROKE_WIDTH = 1;        // Thin outline, adjust as needed

/**
 * Renders a single shape (circle or square) within the SVG canvas.
 * Includes validation for coordinates and an optional stroke for visual distinction.
 *
 * @param {object} props
 * @param {string} props.id - Unique identifier for the shape (used for logging and React key).
 * @param {'blueCircle'|'greenCircle'|'blueSquare'} props.type - The type of shape to render.
 * @param {number} props.x - The x-coordinate for the shape's center (or top-left for rect).
 * @param {number} props.y - The y-coordinate for the shape's center (or top-left for rect).
 * @param {number} [props.size=30] - The diameter/width/height of the shape.
 * @returns {React.ReactElement|null} The SVG element for the shape or null if coordinates are invalid.
 */
const Shape = ({ id, type, x, y, size = 30 }) => {

    // --- Coordinate Validation ---
    // Check if x or y are not valid numbers (including NaN).
    if (typeof x !== 'number' || isNaN(x) || typeof y !== 'number' || isNaN(y)) {
        // Log an error to the console to help debug the source of the invalid coordinate.
        console.error(`[Shape Component] Invalid coordinate received for item id "${id}" (type: ${type}). x: ${x}, y: ${y}. Skipping render for this shape.`);
        // Return null to prevent attempting to render this specific shape, thus avoiding the SVG error.
        return null;
    }
    // --- End Validation ---


    // --- Common SVG Properties ---
    // Calculate properties needed for both circles and rectangles based on valid x, y, size.
    const commonProps = {
        cx: x,                  // SVG circle center x
        cy: y,                  // SVG circle center y
        r: size / 2,            // SVG circle radius
        width: size,            // SVG rect width
        height: size,           // SVG rect height
        squareX: x - size / 2,  // SVG rect top-left x (to center the square)
        squareY: y - size / 2,  // SVG rect top-left y (to center the square)
        stroke: STROKE_COLOR,   // Outline color
        strokeWidth: STROKE_WIDTH,// Outline thickness
    };

    // --- Render SVG Element Based on Type ---
    switch (type) {
        case 'blueCircle':
            return (
                <circle
                    cx={commonProps.cx}
                    cy={commonProps.cy}
                    r={commonProps.r}
                    fill={COLORS.blue}
                    stroke={commonProps.stroke}
                    strokeWidth={commonProps.strokeWidth}
                />
            );
        case 'greenCircle':
            return (
                <circle
                    cx={commonProps.cx}
                    cy={commonProps.cy}
                    r={commonProps.r}
                    fill={COLORS.green}
                    stroke={commonProps.stroke}
                    strokeWidth={commonProps.strokeWidth}
                />
            );
        case 'blueSquare':
            return (
                <rect
                    x={commonProps.squareX}
                    y={commonProps.squareY}
                    width={commonProps.width}
                    height={commonProps.height}
                    fill={COLORS.blue}
                    rx="3" // Optional: Slightly rounded corners for squares
                    stroke={commonProps.stroke}
                    strokeWidth={commonProps.strokeWidth}
                />
            );
        default:
            // Log a warning if the type is unknown, but still return null to prevent errors.
            console.warn(`[Shape Component] Unknown shape type received: ${type} for id: ${id}`);
            return null;
    }
};

// PropTypes validation for the Shape component
Shape.propTypes = {
    id: PropTypes.string.isRequired, // ID is required for key and logging
    type: PropTypes.oneOf(['blueCircle', 'greenCircle', 'blueSquare']).isRequired,
    // Coordinates are technically required but checked internally, so not marked isRequired here
    x: PropTypes.number,
    y: PropTypes.number,
    size: PropTypes.number, // Optional size override
};


/**
 * Renders the main visual stimulus display area containing multiple shapes.
 * It receives an array of item objects, each describing a shape's properties.
 *
 * @param {object} props
 * @param {Array<object>} [props.items=[]] - An array of item objects, where each object has `id`, `type`, `x`, `y`, and optionally `size`.
 * @returns {React.ReactElement} The SVG container with rendered shapes or an error message.
 */
const StimulusDisplay = ({ items }) => { // Default prop handles undefined case
    // Define the coordinate system size for the SVG canvas.
    // These should match or be proportional to DISPLAY_WIDTH/HEIGHT in countingSpanUtils.js
    // for consistent positioning logic.
    const viewBoxWidth = 500;
    const viewBoxHeight = 350;

    // --- Input Validation ---
    // Defensive check: Ensure items prop is actually an array before trying to map over it.
    if (!Array.isArray(items)) {
        console.error("[StimulusDisplay] Received invalid 'items' prop (expected array, got):", items);
        // Render a user-friendly error message within the component's space
        return (
            <div className="w-full h-[350px] flex items-center justify-center text-red-500 border border-red-300 rounded bg-red-50 p-4 text-center">
                Error: Could not display stimulus due to invalid data.
            </div>
        );
    }
    // --- End Input Validation ---

    return (
        // Container div for layout and accessibility attributes
        <div
            className="w-full h-full flex items-center justify-center my-4"
            // Hide the purely decorative SVG content from screen readers
            aria-hidden="true"
        >
            {/* SVG Canvas */}
            <svg
                // Defines the internal coordinate system (0,0 top-left to 500,350 bottom-right)
                viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}
                // Controls how the SVG scales if its container size differs from the viewBox aspect ratio
                preserveAspectRatio="xMidYMid meet"
                // Basic styling for the SVG container itself
                className="max-w-full max-h-[350px] border border-gray-300 rounded bg-gray-50 shadow-sm"
            >
                {/* Map through the validated items array and render a Shape component for each item */}
                {items.map((item) => (
                    // Add another layer of checking: ensure item is an object and has an id
                    // before attempting to render the Shape component.
                    item && typeof item === 'object' && item.id ? (
                        <Shape
                            // Use the unique item ID as the React key for efficient updates
                            key={item.id}
                            // Pass all necessary props down to the Shape component
                            id={item.id} // Pass id for logging within Shape
                            type={item.type}
                            x={item.x} // Pass coordinate (may be invalid, Shape handles it)
                            y={item.y} // Pass coordinate (may be invalid, Shape handles it)
                            size={item.size} // Pass optional size if defined
                        />
                    ) : (
                         // If an item in the array is malformed (null, undefined, or missing id),
                         // log a warning and render nothing for that specific item.
                         console.warn("[StimulusDisplay] Invalid item found in items array (skipped rendering):", item),
                         null // Render nothing for this invalid item entry
                    )
                ))}
            </svg>
        </div>
    );
};

// PropTypes validation for the StimulusDisplay component
StimulusDisplay.propTypes = {
    // Expects an array of objects. Use `arrayOf` and `shape` for detailed validation.
    items: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired, // Each item must have a unique string id
            type: PropTypes.string.isRequired, // Each item must have a type string
            // Coordinates are validated within Shape, so not marked isRequired here
            x: PropTypes.number,
            y: PropTypes.number,
            size: PropTypes.number, // Optional size per item
        })
    ), // The items prop itself is optional, will use defaultProps if not provided
};

// Set default props to ensure `items` is always an array, even if the prop isn't passed.
StimulusDisplay.defaultProps = {
    items: [],
};


export default StimulusDisplay;